import { createApiModule } from '../apiFactory';
import axiosWrapper from '../axiosWrapper';
import type {
  SettingParams,
  SettingResponse,
  UpdateSettingParams,
  GetS3AccessTokenParams,
  S3AccessTokenResponse,
  GetShopListParams,
  GetShopListResponse,
  GetShopLanguageSettingsParams,
  ShopLanguageSettingsResponse,
  GetPageMenuLocalParams,
  GetPageMenuLocalResponse,
  GetS3UploadUrlParams,
  S3UploadUrlResponse
} from './types';

/**
 * 获取店铺设置
 * @param shopDomain 店铺域名
 * @returns 设置数据
 */
export const fetchShopSettings = async (shopDomain: string): Promise<SettingResponse> => {
  try {
    const response = await axiosWrapper.request<SettingResponse>({
      url: '/api/brand/v1/shop/getShopSettings',
      method: 'POST',
      data: { shopDomain }
    });
    
    return response as SettingResponse;
  } catch (error) {
    console.error('获取店铺设置失败:', error);
    throw error;
  }
};

/**
 * 更新店铺设置
 * @param params 更新参数
 * @returns 更新结果
 */
export const updateShopSettings = async (params: UpdateSettingParams): Promise<unknown> => {
  try {
    const response = await axiosWrapper.request<unknown>({
      url: '/api/brand/v1/shop/updateShopSettings',
      method: 'POST',
      data: params
    });
    
    return response;
  } catch (error) {
    console.error('更新店铺设置失败:', error);
    throw error;
  }
};

/**
 * 获取店铺列表
 * @param params 请求参数，可指定是否包含embed信息
 * @returns 返回店铺列表和品牌信息
 */
export const getShopList = async (params: GetShopListParams = {}): Promise<GetShopListResponse> => {
  try {
    const response = await axiosWrapper.request<GetShopListResponse>({
      url: '/api/brand/v1/getShopList',
      method: 'POST',
      data: params
    });
    
    return response as GetShopListResponse;
  } catch (error) {
    console.error('获取店铺列表失败:', error);
    throw error;
  }
};

/**
 * 获取店铺多语言设置
 * @param params 请求参数，包含shopDomain和langCode
 * @returns 返回店铺多语言设置数据
 */
export const getShopLanguageSettings = async (params: GetShopLanguageSettingsParams): Promise<ShopLanguageSettingsResponse> => {
  try {
    const response = await axiosWrapper.request<ShopLanguageSettingsResponse>({
      url: '/api/brand/v1/shop/getShopLanguageSettings',
      method: 'POST',
      data: params
    });
    
    return response as ShopLanguageSettingsResponse;
  } catch (error) {
    console.error('获取店铺多语言设置失败:', error);
    throw error;
  }
};

/**
 * 获取S3访问令牌
 * @returns S3访问令牌响应
 */
export const getS3AccessToken = async (params: GetS3AccessTokenParams): Promise<S3AccessTokenResponse> => {
  try {
    const response = await axiosWrapper.request<S3AccessTokenResponse>({
      url: '/api/brand/v1/system/getS3AccessToken',
      method: 'POST',
      data: params
    });
    
    return response as S3AccessTokenResponse;
  } catch (error) {
    console.error('获取S3访问令牌失败:', error);
    throw error;
  }
};

/**
 * 获取页面菜单本地化
 * @param params 请求参数，包含语言代码
 * @returns 本地化菜单数据
 */
export const getPageMenuLocal = async (params: GetPageMenuLocalParams): Promise<GetPageMenuLocalResponse> => {
  try {
    const response = await axiosWrapper.request<GetPageMenuLocalResponse>({
      url: '/api/brand/v1/system/getPageMenuLocal',
      method: 'POST',
      data: params
    });
    
    return response as GetPageMenuLocalResponse;
  } catch (error) {
    console.error('获取页面菜单本地化失败:', error);
    throw error;
  }
};

/**
 * 创建API模块
 */
export const settingApi = createApiModule('shop', {
  getShopSettings: { method: 'POST', urlSuffix: '/getShopSettings' },
  updateShopSettings: { method: 'POST', urlSuffix: '/updateShopSettings' }
});

/**
 * 系统API模块
 */
export const systemApi = createApiModule('system', {
  getS3AccessToken: { method: 'POST', urlSuffix: '/getS3AccessToken', errorMessage: '获取S3访问令牌失败' },
  getPageMenuLocal: { method: 'POST', urlSuffix: '/getPageMenuLocal', errorMessage: '获取页面导航本地化失败' },
  getS3UploadUrl: { 
    method: 'POST', 
    urlSuffix: '/getS3UploadUrl', 
    errorMessage: '获取S3文件上传URL失败'
  }
});

/**
 * 步骤Key常量
 */
export const StepKeys = {
  ENABLE_APP_EMBED: 'stepKeyEnableAppEmbed',
  SET_STYLE_WIDGET: 'stepKeySetStyleWidget',
  CUSTOMIZE_HELP_CENTER: 'stepKeyCustomizeHelpCenter'
};

/**
 * 步骤状态常量
 */
export const StepStatus = {
  NOT_STARTED: 0,
  PARTIAL: 1,
  COMPLETED: 2,
  SKIPPED: 3
};

/**
 * 店铺API模块
 */
export const shopApi = createApiModule('shop', {
  getShopSettings: { method: 'POST', urlSuffix: '/getShopSettings' },
  updateShopSettings: { method: 'POST', urlSuffix: '/updateShopSettings' },
  getShopList: { method: 'POST', urlSuffix: '/getShopList', errorMessage: '获取店铺列表失败' },
  getShopLanguageSettings: { method: 'POST', urlSuffix: '/getShopLanguageSettings', errorMessage: '获取店铺多语言设置失败' }
});

/**
 * 获取S3文件上传URL
 * @param params 请求参数，包含店铺域名和文件信息
 * @returns S3文件上传URL响应
 */
export const getS3UploadUrl = async (params: GetS3UploadUrlParams): Promise<S3UploadUrlResponse> => {
  try {
    const response = await axiosWrapper.request<S3UploadUrlResponse>({
      url: '/api/brand/v1/system/getS3UploadUrl',
      method: 'POST',
      data: params
    });
    
    return response as S3UploadUrlResponse;
  } catch (error) {
    console.error('获取S3文件上传URL失败:', error);
    throw error;
  }
}; 